/**
 * /venues/:venueSlug/admin/tournaments/:tournamentId — running one night.
 *
 * The boards panel sits ABOVE the tabs and outside them. An admin walking the
 * room needs to see the boards whatever screen they last left open, and a
 * boards tab is a boards tab you are not on at the moment something goes
 * wrong.
 *
 * Two data paths, deliberately:
 *   - REST for everything a reload must restore (entries, standings, money).
 *   - the TournamentRoom socket for the boards, because live scores and whose
 *     throw it is exist nowhere else. See src/lib/tournamentRoom.js.
 *
 * A tournament has NO NAME COLUMN. Identity is night number, date and season,
 * which is what the header assembles.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiGet, apiPost } from '../lib/api.js';
import { useVenue } from '../context/AuthContext.jsx';
import { formatDate } from '../lib/format.js';
import StatusBadge from '../components/StatusBadge.jsx';
import { useTournamentRoom } from '../lib/tournamentRoom.js';

import BoardsPanel     from '../components/tournament/BoardsPanel.jsx';
import HeldVisitsPanel from '../components/tournament/HeldVisitsPanel.jsx';
import TotalRaisedPanel from '../components/tournament/TotalRaisedPanel.jsx';
import StartTournamentModal from '../components/tournament/StartTournamentModal.jsx';
import EntriesTab    from '../components/tournament/EntriesTab.jsx';
import CheckInTab    from '../components/tournament/CheckInTab.jsx';
import FixturesTab   from '../components/tournament/FixturesTab.jsx';
import StandingsTab  from '../components/tournament/StandingsTab.jsx';
import WaitlistTab   from '../components/tournament/WaitlistTab.jsx';
import PaymentsTab   from '../components/tournament/PaymentsTab.jsx';
import SettingsTab   from '../components/tournament/SettingsTab.jsx';

const TABS = [
  { id: 'entries',   label: 'Entries' },
  { id: 'checkin',   label: 'Check-in' },
  { id: 'fixtures',  label: 'Fixtures' },
  { id: 'standings', label: 'Standings' },
  { id: 'waitlist',  label: 'Waitlist' },
  { id: 'payments',  label: 'Payments' },
  { id: 'settings',  label: 'Settings' },
];

/* ------------------------------------------------------------------------- */

/**
 * Which player is conceding this match.
 *
 * Asked rather than assumed: "Forfeit" on a board is not a statement about
 * which of the two it was, and picking the wrong one hands the tie to the
 * wrong player.
 */
function ForfeitPrompt({ match, busy, error, onConfirm, onCancel }) {
  return (
    <div className="hd-card hd-stack">
      <h3 className="hd-section-title" style={{ margin: 0 }}>
        Forfeit on board {match.board_number}
      </h3>
      <p className="hd-muted">
        The other player takes the match. This forfeits THIS match only.
      </p>

      {error && <div className="hd-badge hd-badge-danger" role="alert">{error}</div>}

      <div className="hd-row">
        {[
          { id: match.player1_id, name: match.player1_name },
          { id: match.player2_id, name: match.player2_name },
        ].filter((p) => p.id != null).map((p) => (
          <button
            key={p.id}
            type="button"
            className="hd-btn hd-btn-secondary"
            disabled={busy}
            onClick={() => onConfirm(p.id)}
          >
            {p.name} forfeits
          </button>
        ))}

        <button type="button" className="hd-btn hd-btn-quiet" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */

export default function AdminTournamentDetail() {
  const { venueSlug, tournamentId } = useParams();
  const contextVenue = useVenue(venueSlug);
  const contextId = contextVenue?.venue_id ?? null;

  const [venueId, setVenueId] = useState(contextId);
  const [state, setState]     = useState({ status: 'loading' });
  const [tab, setTab]         = useState('entries');

  const [forfeit, setForfeit]         = useState(null);
  const [forfeitBusy, setForfeitBusy] = useState(false);
  const [forfeitError, setForfeitErr] = useState(null);

  const [startOpen, setStartOpen] = useState(false);

  const room = useTournamentRoom(venueId, tournamentId);

  /**
   * Everything the tabs read, in one pass.
   *
   * The waitlist and payments calls are admin-only and the rest are public, so
   * a non-admin landing here gets the page with two empty tabs rather than a
   * failed load — allSettled rather than all for the same reason.
   */
  const load = useCallback(async (id) => {
    const base = `/api/venues/${id}/tournaments/${tournamentId}`;

    const [tournament, entries, standings, bracket, waitlist, payments, matchList] = await Promise.all([
      apiGet(base),
      apiGet(`${base}/entries`),
      apiGet(`${base}/standings`),
      apiGet(`${base}/knockout/bracket`),
      apiGet(`${base}/waitlist`),
      apiGet(`${base}/payments`),
      apiGet(`${base}/matches`),
    ]);

    if (tournament.error) return { status: 'error', error: tournament.error };

    // groups, combined and hasKnockout all come off the ONE /standings call.
    // Which of the two tables the Standings tab shows is the server's decision,
    // carried by hasKnockout — see StandingsTab.
    return {
      status:      'ready',
      tournament:  tournament.data.tournament,
      entries:     entries.data?.entries ?? [],
      groups:      standings.data?.groups ?? [],
      combined:    standings.data?.combined ?? null,
      hasKnockout: standings.data?.hasKnockout ?? false,
      bracket:     bracket.data ?? { bracketSize: 0, rounds: [] },
      matches:     matchList.data?.matches ?? [],
      waitlist:    waitlist.data?.waitlist ?? [],
      payments:    payments.data ?? null,
    };
  }, [tournamentId]);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    (async () => {
      let id = contextId;

      // Platform staff hold no user_venue_roles row, so the auth context has no
      // entry for a venue they can still administer.
      if (id === null) {
        const { data, error } = await apiGet(`/api/venues/by-slug/${venueSlug}`);
        if (cancelled) return;

        if (error || !data?.venue) {
          setState({ status: 'error', error: error ?? 'Venue not found' });
          return;
        }
        id = data.venue.id;
      }

      if (cancelled) return;
      setVenueId(id);

      const next = await load(id);
      if (!cancelled) setState(next);
    })();

    return () => { cancelled = true; };
  }, [venueSlug, contextId, tournamentId, load]);

  const refresh = useCallback(async () => {
    if (venueId == null) return;
    setState(await load(venueId));
  }, [venueId, load]);

  // The bracket can appear without this page having asked for anything: the
  // room generates it the moment the last group match lands on a clean split.
  // Everything the Fixtures and Standings tabs read is REST, so this broadcast
  // is the only signal that any of it has changed.
  useEffect(() => {
    if (room.knockoutGenerated) refresh();
  }, [room.knockoutGenerated, refresh]);

  /**
   * The cost is not visible on the button. A forfeit does not merely award the
   * match — every dart thrown in it stops counting for BOTH players, so a 180
   * the conceding player hit ten minutes ago goes with it. An admin reaching
   * for this to "record the result so far" is reaching for the wrong thing, and
   * the moment before it fires is the only one in which they can be told.
   *
   * Covers both single-match endpoints and nothing else. The night forfeit is
   * another component with its own in-place confirmation.
   */
  const submitForfeit = async (forfeitEntryId) => {
    const conceding = forfeit.player1_id === forfeitEntryId
      ? forfeit.player1_name
      : forfeit.player2_name;

    const ok = window.confirm(
      `${conceding} forfeits this match.\n\n`
      + 'Every dart thrown in this match is discarded for BOTH players — no '
      + 'average, no 180s, no 140+ or 100+ counts, for either of them.\n\n'
      + 'Legs already won still stand, and the other player takes the match. '
      + 'This cannot be undone.'
    );

    if (!ok) return;

    setForfeitBusy(true);
    setForfeitErr(null);

    // Two endpoints, not one. A group forfeit moves the standings and so moves
    // who qualifies; a knockout forfeit advances the winner. The server keeps
    // them apart, so the client has to pick the right one.
    const path = forfeit.stage === 'knockout' ? 'knockout/forfeit' : 'group-forfeit';

    const { error } = await apiPost(
      `/api/venues/${venueId}/tournaments/${tournamentId}/${path}`,
      { matchId: forfeit.id, forfeitEntryId }
    );

    setForfeitBusy(false);
    if (error) { setForfeitErr(error); return; }

    setForfeit(null);
    await refresh();
  };

  if (state.status === 'loading') return <div className="hd-empty">Loading…</div>;

  if (state.status === 'error') {
    return (
      <div className="hd-stack">
        <h1 className="hd-page-title">Tournament</h1>
        <p className="hd-muted">{state.error}</p>
      </div>
    );
  }

  const {
    tournament, entries, groups, combined, hasKnockout, bracket, waitlist, payments, matches,
  } = state;

  // Group membership for the entries table. Derived from the standings rather
  // than fetched: /entries knows nothing about groups, and the standings
  // already carry every drawn player's group number.
  const groupByEntry = {};
  for (const group of groups) {
    for (const row of group.standings) groupByEntry[row.entry_id] = group.groupNumber;
  }

  const title = tournament.night_number != null
    ? `Night ${tournament.night_number}`
    : 'Tournament';

  const tabProps = { venueId, tournamentId, tournament, onChanged: refresh };

  // Only from registration, which is the one status POST .../draw accepts. The
  // draw is not repeatable — it leaves the tournament 'active' — so the button
  // is absent rather than disabled once one exists: there is nothing an admin
  // could usefully do with it, and a greyed-out "Start Tournament" above a
  // tournament already in progress only invites a click.
  const canStart = tournament.status === 'registration';

  return (
    <div className="hd-stack">
      <div className="hd-row-between">
        <div>
          <h1 className="hd-page-title">{title}</h1>
          <p className="hd-meta">
            {contextVenue?.name ?? venueSlug}
            {tournament.date && <> · {formatDate(tournament.date)}</>}
            {' · '}{tournament.entry_count} / {tournament.max_entries} entered
          </p>
        </div>

        <StatusBadge status={tournament.status} />
      </div>

      <div className="hd-row">
        {canStart && (
          <button
            type="button"
            className="hd-btn hd-btn-primary"
            onClick={() => setStartOpen(true)}
          >
            Start Tournament
          </button>
        )}

        {/* WHATEVER THE STATUS. A screen set up before the draw shows the
            venue's artwork and turns into the boards when the draw lands, so
            there is nothing to wait for — and after the night, this is how
            somebody puts the results back up.

            Scoped to THIS tournament by id, which is the whole point of moving
            it off the nav: what opens is the night being looked at, not
            whichever one the venue has in play. A device permanently on the
            wall uses the bare /tv URL and follows the active one instead.

            window.open rather than a link, to match what the nav entry did. */}
        <button
          type="button"
          className="hd-btn hd-btn-secondary"
          onClick={() => window.open(
            `/venues/${venueSlug}/tv?tournament_id=${tournamentId}`,
            '_blank',
            'noopener,noreferrer'
          )}
        >
          View TV Display
        </button>

        {canStart && (
          <span className="hd-hint">
            Confirm who turned up, choose the groups and run the draw.
          </span>
        )}
      </div>

      {startOpen && canStart && (
        <StartTournamentModal
          venueId={venueId}
          tournamentId={tournamentId}
          entries={entries}
          onChanged={refresh}
          onClose={() => setStartOpen(false)}
          onDrawn={() => { setStartOpen(false); setTab('fixtures'); }}
        />
      )}

      <BoardsPanel
        status={room.status}
        snapshot={room.snapshot}
        completedByBoard={room.completedByBoard}
        onForfeit={setForfeit}
      />

      {/* Directly under the boards, and above the tabs, because it is about
          what is happening on a board right now and it renders nothing at all
          unless something needs deciding. The room's heldVisits count drives
          the reload, so a stray dart surfaces while the page is open. */}
      <HeldVisitsPanel
        venueId={venueId}
        tournamentId={tournamentId}
        refreshKey={room.snapshot?.heldVisits ?? 0}
        onResolved={refresh}
      />

      {/* Below the held visits, because those are about darts that have been
          thrown and this is about a presentation. Renders nothing at all
          unless the venue has turned the feature on. */}
      <TotalRaisedPanel
        venueId={venueId}
        tournamentId={tournamentId}
        snapshot={room.snapshot}
      />

      {forfeit && (
        <ForfeitPrompt
          match={forfeit}
          busy={forfeitBusy}
          error={forfeitError}
          onConfirm={submitForfeit}
          onCancel={() => { setForfeit(null); setForfeitErr(null); }}
        />
      )}

      <div className="hd-tabs" role="tablist">
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`hd-tab ${tab === id ? 'hd-tab-active' : ''}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'entries' && (
        <EntriesTab {...tabProps} entries={entries} groupByEntry={groupByEntry} />
      )}
      {tab === 'checkin'   && <CheckInTab   {...tabProps} entries={entries} />}
      {tab === 'fixtures'  && <FixturesTab {...tabProps} matches={matches} bracket={bracket} />}
      {tab === 'standings' && (
        <StandingsTab groups={groups} combined={combined} hasKnockout={hasKnockout} />
      )}
      {tab === 'waitlist'  && <WaitlistTab  {...tabProps} waitlist={waitlist} />}
      {tab === 'payments'  && <PaymentsTab  {...tabProps} payments={payments} />}
      {tab === 'settings'  && <SettingsTab  {...tabProps} />}
    </div>
  );
}
